import { FieldArray } from 'formik';
import { map, uniqueId } from 'lodash';
import React, { useContext, useRef } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { DraggableContainer } from '../../../../../shared/DraggableContainer';
import { DroppableContainer } from '../../../../../shared/DroppableContainer';
import { FormContext } from '../../../../../shared/form/Form';
import { colourNeutral03, colourNeutral07, colourNeutral08, colourNeutral09 } from '../../../../../styling/colours';
import { fontSize01, fontWeightBold } from '../../../../../styling/fonts';
import { spacing03 } from '../../../../../styling/spacing';
import { DesignMaterialsCalculationsRequest } from '../../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { BuildTypeCode } from '../../../../projects/project';
import { AddItemButton } from '../../AddItemButton';
import { isQuoteFormContextValue, QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { manifoldFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { QuoteOrDesignFormType } from '../../quoteOrDesignFormType';
import { getInitialDesignRoomDetailsFormModel } from '../../rooms/designRoomDetailsFormModel';
import { getNewRoomHeatLossDetailsFormModel } from '../../rooms/heatLoss/roomHeatLossDetailsFormModel';
import { createDuplicateRoom, RoomFields } from '../../rooms/RoomFields';
import {
  defaultFloorLevelCode,
  defaultThermostatTypeCode,
  defaultThermostatTypeCodeForRadiatorManifold,
  RoomFormModel,
} from '../../rooms/roomFormModel';
import { UnitFormModel } from '../../units/unitFormModel';
import { ManifoldFormModel, RoomlessManifoldFormModel } from '../manifoldFormModel';

type Props = {
  formType: QuoteOrDesignFormType;
  buildTypeCode: BuildTypeCode;
  manifold: ManifoldFormModel;
  unit: UnitFormModel;
  unitIndex: number;
  manifoldIndex: number;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
};

export const ManifoldRoomsField = ({
  formType,
  buildTypeCode,
  manifold,
  unit,
  unitIndex,
  manifoldIndex,
  designMaterialsCalculationsRequest,
}: Props) => {
  const quoteOrDesignFormContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = quoteOrDesignFormContext.formProps;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const quoteOrDesignThermostatTypeCode = isQuoteFormContextValue(quoteOrDesignFormContext)
    ? quoteOrDesignFormContext.formProps.values.thermostatTypeCode
    : quoteOrDesignFormContext.formProps.values.defaultThermostatTypeCode;

  const createRoomlessManifold = (): RoomlessManifoldFormModel => ({
    floorLevelCode: null,
    floorConstruction: null,
    areaInMetresSquared: '',
    numberOfPorts: '',
    thermostatTypeCode: quoteOrDesignThermostatTypeCode,
    numberOfThermostats: '',
    numberOfWetSensors: '',
    numberOfFloorSensors: '',
  });

  const initialiseRoomlessManifold = () => {
    setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'roomlessManifold'), createRoomlessManifold());
    setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'rooms'), null);
  };

  const numberOfRoomsCreated = useRef(manifold.rooms != null ? manifold.rooms.length : 0);

  const createNewRoom = (): RoomFormModel => {
    numberOfRoomsCreated.current += 1;

    return {
      roomId: null,
      id: uniqueId('room'),
      name: `Room ${numberOfRoomsCreated.current}`,
      floorLevelCode: defaultFloorLevelCode,
      floorConstruction: null,
      areaInMetresSquared: '',
      thermostatTypeCode: manifold.isRadiator
        ? defaultThermostatTypeCodeForRadiatorManifold
        : quoteOrDesignThermostatTypeCode,
      thermostatSharedWithId: null,
      numberOfPorts: '',
      roomTemperature: '',
      roomType: null,
      numberOfThermostats: manifold.isRadiator ? '0' : '1',
      numberOfWetSensors: '0',
      numberOfFloorSensors: '0',
      perimeterInMetres: '',
      heatLossDetails:
        unit.heatLossDetails == null
          ? null
          : getNewRoomHeatLossDetailsFormModel(
              /* floorConstruction: */ null,
              defaultFloorLevelCode,
              /* roomType: */ null,
              unit.heatLossDetails,
              unit,
              buildTypeCode,
              quoteOrDesignFormContext.externalAirTemperatureInDegreesCelsius,
            ),
      designRoomDetails: formType === 'Design' ? getInitialDesignRoomDetailsFormModel(buildTypeCode, null, null) : null,
    };
  };

  return (
    <DroppableContainer droppableId={manifold.id} type="ROOM">
      {placeholder => {
        return (
          <FieldArray name={manifoldFieldName(unitIndex, manifoldIndex, 'rooms')}>
            {arrayHelpers => {
              const addRoom = () => {
                arrayHelpers.push(createNewRoom());
              };

              const duplicateRoomAtIndex = (index: number) => {
                const newRoom = createDuplicateRoom(assertNotNull(manifold.rooms)[index]);
                arrayHelpers.insert(index + 1, newRoom);
              };

              return (
                <>
                  <RoomsContainer>
                    {map(manifold.rooms, (room, index) => {
                      return (
                        <StyledDraggableContainer key={room.id} draggableId={room.id} index={index}>
                          {(roomDragHandleProps: DraggableProvidedDragHandleProps | null) => (
                            <RoomFields
                              unitIndex={unitIndex}
                              unit={unit}
                              manifoldIndex={manifoldIndex}
                              manifold={manifold}
                              roomIndex={index}
                              room={room}
                              dragHandleProps={roomDragHandleProps}
                              deleteRoom={() => arrayHelpers.remove(index)}
                              duplicateRoom={() => duplicateRoomAtIndex(index)}
                              designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
                            />
                          )}
                        </StyledDraggableContainer>
                      );
                    })}
                    {placeholder}
                  </RoomsContainer>
                  {!isInReadOnlyMode &&
                    ((manifold.rooms == null || manifold.rooms.length === 0) &&
                    manifold.roomlessManifold == null &&
                    !manifold.isRadiator ? (
                      <EnterDetailsOrAddRoomChoiceContainer>
                        <AddRoomButton
                          onClick={addRoom}
                          data-testid={manifoldAddRoomButtonTestId(unitIndex, manifoldIndex)}
                        >
                          + Add Room
                        </AddRoomButton>
                        <OrText>or</OrText>
                        <InputManifoldDetailsButton
                          onClick={initialiseRoomlessManifold}
                          data-testid={manifoldEnterDetailsButtonTestId(unitIndex, manifoldIndex)}
                        >
                          Enter details for Manifold
                        </InputManifoldDetailsButton>
                      </EnterDetailsOrAddRoomChoiceContainer>
                    ) : (
                      <AddRoomButton
                        onClick={addRoom}
                        data-testid={manifoldAddRoomButtonTestId(unitIndex, manifoldIndex)}
                      >
                        + Add Room
                      </AddRoomButton>
                    ))}
                </>
              );
            }}
          </FieldArray>
        );
      }}
    </DroppableContainer>
  );
};

export const manifoldEnterDetailsButtonTestId = (unitIndex: number, manifoldIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-enter-details-button`;

export const manifoldAddRoomButtonTestId = (unitIndex: number, manifoldIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-add-room`;

const AddRoomButton = styled(AddItemButton)`
  margin-top: ${spacing03};
`;

const RoomsContainer = styled.div`
  background-color: ${colourNeutral07};
`;

const StyledDraggableContainer = styled(DraggableContainer)`
  background-color: ${colourNeutral09};
  border: solid 1px ${colourNeutral08};
`;

const InputManifoldDetailsButton = styled(AddItemButton)`
  margin-top: ${spacing03};
`;

const EnterDetailsOrAddRoomChoiceContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const OrText = styled.div`
  width: 140px;
  color: ${colourNeutral03};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${fontWeightBold};
  font-size: ${fontSize01};
`;
